/**
 * AI Assistant Panel
 *
 * Context-aware AI generation:
 * - Project context (no artifact selected): Generate master plan to project.stages.PLAN
 * - Artifact context (artifact selected): Generate artifact plan to artifact.stages.ARTIFACT_PLAN
 *
 * 3-stage AI-assisted workflow:
 * 1. User Intent → Plan (with approval)
 * 2. Plan → LumaScript (with approval)
 * 3. LumaScript → Preview (deterministic)
 */

import { useState, useEffect, useRef } from 'react';
import { ComponentContainer } from 'golden-layout';
import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { useAuditStore } from '@/stores/auditStore';
import { useProcessMonitor } from '@/stores/processMonitorStore';
import { Button } from '../ui/Button';
import { v4 as uuidv4 } from 'uuid';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Sparkles,
  Clock,
  Settings
} from 'lucide-react';
import { api } from '@/lib/api';
import { API_BASE_URL } from '@/lib/config';
import { useUserStore } from '@/stores/userStore';
import { buildUserHeaders } from '@/stores/userStore';

type GenerationStage = 'initial' | 'plan' | 'script' | 'bpmn' | 'complete';

interface GenerationState {
  skillId?: string;
  currentStage: GenerationStage;
  userIntent?: string;
  generatedPlan?: string;
  planApproved: boolean;
  generatedScript?: string;
  scriptApproved: boolean;
  generatedBpmn?: string;
  bpmnApproved: boolean;
  isGenerating: boolean;
  error?: string;
}

interface AIAssistantPanelProps {
  container?: ComponentContainer;
}

// Helper function to strip markdown code fences from LLM responses
const stripMarkdownCodeFences = (text: string): string => {
  // Remove ```json ... ``` or ``` ... ``` wrapper
  const match = text.match(/^```(?:json)?\s*\n([\s\S]*?)\n```$/);
  if (match) {
    return match[1].trim();
  }
  return text.trim();
};

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = () => {
  const [userIntent, setUserIntent] = useState('');
  const [generationState, setGenerationState] = useState<GenerationState>({
    currentStage: 'initial',
    planApproved: false,
    scriptApproved: false,
    bpmnApproved: false,
    isGenerating: false
  });
  const [streamedContent, setStreamedContent] = useState('');
  const [sessionId] = useState(() => uuidv4()); // Generate session ID once on mount
  const [, setTimerTick] = useState(0); // Force re-render for timer updates
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null); // null = loading, true/false = checked
  const contentRef = useRef<HTMLDivElement>(null);

  // Use projectStore instead of editorStore
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const getSelectedArtifact = useProjectStore((state) => state.getSelectedArtifact);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);

  const { addToast } = useToastStore();
  const { startGeneration, completeGeneration, failGeneration, getActiveGeneration, fetchLogs } = useAuditStore();
  const { addProcess, updateProcess } = useProcessMonitor();
  const activeUserId = useUserStore((state) => state.activeUserId) || 'default-user';

  // Context detection
  const isProjectContext = !selectedArtifactId;
  const project = activeProjectId ? projects[activeProjectId] : null;

  // Check for API keys on component mount
  useEffect(() => {
    const checkApiKeys = async () => {
      try {
        const settingsResponse = await api.get(`/settings/${activeUserId}`);
        const settings = settingsResponse.data.settings;
        const hasKeys = !!(settings?.has_openai_key || settings?.has_anthropic_key);
        setHasApiKey(hasKeys);
      } catch (error) {
        console.error('Failed to check API keys:', error);
        setHasApiKey(false); // Assume no keys on error
      }
    };
    checkApiKeys();
  }, []);

  // Auto-scroll to bottom when content updates
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamedContent, generationState]);

  // Update timer every second during generation
  useEffect(() => {
    if (!generationState.isGenerating) return;

    const timer = setInterval(() => {
      setTimerTick(tick => tick + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [generationState.isGenerating]);

  // Load generation state for active skill
  useEffect(() => {
    if (selectedArtifactId) {
      loadGenerationState(selectedArtifactId);
    }
  }, [selectedArtifactId]);

  const loadGenerationState = async (artifactId: string) => {
    try {
      // Load artifact stages from new API
      const response = await api.get(`/generation/${artifactId}`);
      const stages = response.data.stages || [];

      // Find PROMPT and PLAN stages
      const promptStage = stages.find((s: any) => s.stage === 'PROMPT');
      const planStage = stages.find((s: any) => s.stage === 'PLAN');
      const scriptStage = stages.find((s: any) => s.stage === 'LUMASCRIPT');

      // Always show initial state when not actively generating
      // This prevents the AI Assistant from showing stale content when user navigates between panels
      setGenerationState({
        currentStage: 'initial',
        userIntent: undefined,
        generatedPlan: undefined,
        planApproved: false,
        generatedScript: undefined,
        scriptApproved: false,
        bpmnApproved: false,
        isGenerating: false
      });
    } catch (error) {
      // No existing state or error fetching, start fresh
      console.log('No existing generation state, starting fresh');
      setGenerationState({
        currentStage: 'initial',
        planApproved: false,
        scriptApproved: false,
        bpmnApproved: false,
        isGenerating: false
      });
    }
  };

  // Save plan to artifact_stages
  const savePlanToStages = async (artifactId: string, plan: string) => {
    try {
      await api.post('/generation/plan/save', {
        artifactId,
        plan
      });
    } catch (error) {
      console.error('Failed to save plan to stages:', error);
    }
  };

  // Stage 1: Generate Plan from User Intent
  const generatePlan = async () => {
    if (!userIntent.trim()) return;
    if (!isProjectContext && !selectedArtifactId) return; // Artifact context needs artifact selected

    setGenerationState((prev) => ({
      ...prev,
      isGenerating: true,
      currentStage: 'plan',
      userIntent,
      error: undefined
    }));
    setStreamedContent('');

    // Start audit log tracking
    let auditId = '';
    try {
      // Get user settings for LLM configuration
      const settingsResponse = await api.get(`/settings/${activeUserId}`);
      const settings = settingsResponse.data.settings;

      if (!settings || (!settings.has_openai_key && !settings.has_anthropic_key)) {
        throw new Error('No LLM API key configured. Please go to Settings to add one.');
      }

      // Track generation start in audit log
      const modelProvider = settings.active_provider || 'openai';
      const modelName = modelProvider === 'openai' ? settings.openai_model : settings.anthropic_model;

      // Use project ID or artifact ID for audit tracking
      const targetId = isProjectContext ? activeProjectId : selectedArtifactId;
      auditId = await startGeneration(targetId || 'unknown', 'plan', modelProvider, modelName);

      // Call streaming API (works for both project and artifact contexts)
      const response = await fetch(`${API_BASE_URL}/generation/plan`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          artifactId: selectedArtifactId || undefined,
          projectId: isProjectContext ? activeProjectId : undefined,
          prompt: userIntent
        })
      });

      if (!response.ok) {
        // Try to parse error response from API
        try {
          const errorData = await response.json();
          if (errorData.errorType === 'configuration') {
            throw new Error(`⚙️ Configuration Error: ${errorData.error}\n\nPlease click the Settings button in the top-right corner to configure your API keys.`);
          }
          throw new Error(errorData.error || `Failed to generate plan: ${response.statusText}`);
        } catch (parseError) {
          // If JSON parsing fails, use status text
          throw new Error(`Failed to generate plan: ${response.statusText}`);
        }
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullPlan = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          fullPlan += chunk;
          setStreamedContent(fullPlan);
        }
      }

      // Update state with completed plan
      setGenerationState((prev) => ({
        ...prev,
        generatedPlan: fullPlan,
        isGenerating: false
      }));
      setStreamedContent('');

      // Save plan based on context
      if (activeProjectId) {
        if (isProjectContext) {
          // Save to project-level PLAN stage
          await updateProjectStage(activeProjectId, 'PLAN', fullPlan, false);
          setActiveStage('PLAN');
        } else if (selectedArtifactId) {
          // Save to artifact-level ARTIFACT_PLAN stage
          await savePlanToStages(selectedArtifactId, fullPlan);
          await updateArtifactStage(activeProjectId, selectedArtifactId, 'ARTIFACT_PLAN', fullPlan, false);
          setActiveStage('ARTIFACT_PLAN');
        }
      }

      // Complete audit log with artifact size
      const artifactSize = new Blob([fullPlan]).size;
      await completeGeneration(auditId, artifactSize);
    } catch (error: any) {
      console.error('Generate plan error:', error);

      // Fail audit log
      await failGeneration(auditId, error.message || 'Failed to generate plan');

      // Show error toast
      if (error.message.includes('No LLM API key configured')) {
        addToast('error', 'No API key configured. Please go to Settings (⚙️) to add your OpenAI or Anthropic API key.', 8000);
      } else {
        addToast('error', error.message || 'Failed to generate plan', 6000);
      }

      setGenerationState((prev) => ({
        ...prev,
        isGenerating: false,
        error: error.message || 'Failed to generate plan'
      }));
    }
  };

  // Stage 2: Generate LumaScript from Plan
  const generateScript = async () => {
    if (!generationState.generatedPlan || !selectedArtifactId) return;

    setGenerationState((prev) => ({
      ...prev,
      isGenerating: true,
      currentStage: 'script',
      error: undefined
    }));
    setStreamedContent('');

    // Start audit log tracking
    let auditId = '';
    try {
      // Get LLM configuration for audit log
      const settingsResponse = await api.get(`/settings/${activeUserId}`);
      const settings = settingsResponse.data.settings;
      const modelProvider = settings?.active_provider || 'openai';
      const modelName = modelProvider === 'openai' ? settings?.openai_model : settings?.anthropic_model;
      auditId = await startGeneration(selectedArtifactId, 'lumascript', modelProvider, modelName);
      const response = await fetch(`${API_BASE_URL}/generation/script`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          artifactId: selectedArtifactId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate script: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullScript = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          fullScript += chunk;
          setStreamedContent(fullScript);
        }
      }

      // Strip markdown code fences before storing in state
      const cleanedScript = stripMarkdownCodeFences(fullScript);

      setGenerationState((prev) => ({
        ...prev,
        generatedScript: cleanedScript, // Store cleaned script, not raw fullScript
        isGenerating: false
      }));
      setStreamedContent('');

      // Parse and save script to artifact stages so it shows in LumaScript Editor Panel AND persists to database
      if (activeProjectId) {
        try {
          const scriptJson = JSON.parse(cleanedScript);
          await updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', JSON.stringify(scriptJson), false);
        } catch (parseError) {
          console.error('Failed to parse LumaScript JSON:', parseError);
          // Save as string if JSON parse fails
          await updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', cleanedScript, false);
        }
      }

      // Complete audit log with artifact size
      const artifactSize = new Blob([cleanedScript]).size;
      await completeGeneration(auditId, artifactSize);

      // Switch to LumaScript stage
      setActiveStage('LUMASCRIPT');
    } catch (error: any) {
      console.error('Generate script error:', error);

      // Fail audit log
      await failGeneration(auditId, error.message || 'Failed to generate LumaScript');

      addToast('error', error.message || 'Failed to generate LumaScript', 6000);
      setGenerationState((prev) => ({
        ...prev,
        isGenerating: false,
        error: error.message || 'Failed to generate script'
      }));
    }
  };

  // Stage 3: Generate BPMN from LumaScript (async with job polling)
  const generateBPMN = async () => {
    if (!generationState.generatedScript || !selectedArtifactId) return;

    setGenerationState((prev) => ({
      ...prev,
      isGenerating: true,
      currentStage: 'bpmn',
      error: undefined
    }));

    // Start audit log tracking
    let auditId = '';
    let jobId = ''; // Declare in outer scope so catch block can access it
    try {
      // Get LLM configuration for audit log
      const settingsResponse = await api.get(`/settings/${activeUserId}`);
      const settings = settingsResponse.data.settings;
      const modelProvider = settings?.active_provider || 'openai';
      const modelName = modelProvider === 'openai' ? settings?.openai_model : settings?.anthropic_model;
      auditId = await startGeneration(selectedArtifactId, 'bpmn', modelProvider, modelName);

      // Start async BPMN generation job
      const jobResponse = await api.post('/transforms/lumascript-to-bpmn', {
        artifactId: selectedArtifactId,
        lumascript: generationState.generatedScript,
        userId: activeUserId
      });

      jobId = jobResponse.data.jobId;
      console.log(`BPMN generation started. Job ID: ${jobId}`);

      // Register process in monitor
      const selectedArtifact = getSelectedArtifact();
      const processInfo = {
        id: jobId,
        type: 'bpmn' as const,
        skillId: selectedArtifactId,  // processMonitorStore still uses skillId internally
        skillName: selectedArtifact?.name || 'Unknown Artifact',
        startedAt: Date.now(),
        status: 'queued' as const,
        modelInfo: {
          provider: modelProvider as 'openai' | 'anthropic',
          selectedModel: modelName || 'unknown'
        }
      };
      console.log('Adding process to monitor:', processInfo);
      addProcess(processInfo);

      // Poll job status until completion (support up to 10 minute AI responses)
      let jobCompleted = false;
      let attempts = 0;
      const maxAttempts = 600; // 600 seconds (10 minutes) - support slow reasoning models
      const pollInterval = 1000; // 1 second

      while (!jobCompleted && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

        // Show progress every 30 seconds for long-running jobs
        if (attempts % 30 === 0) {
          const elapsed = Math.floor(attempts / 60);
          console.log(`BPMN generation still in progress... (${elapsed}m ${attempts % 60}s elapsed)`);
        }

        const statusResponse = await api.get(`/jobs/${jobId}`);
        const job = statusResponse.data;

        // Update process monitor with current status
        if (job.status === 'processing') {
          updateProcess(jobId, { status: 'processing' });
        }

        if (job.status === 'completed') {
          jobCompleted = true;
          console.log(`BPMN generation completed after ${attempts} seconds`);

          // Reload skill to get the BPMN from database
          const skillResponse = await api.get(`/skills/${selectedArtifactId}`);
          const bpmn = skillResponse.data.bpmn;

          if (!bpmn) {
            throw new Error('BPMN generation completed but no BPMN data found. Please try again.');
          }

          setGenerationState((prev) => ({
            ...prev,
            generatedBpmn: bpmn,
            currentStage: 'complete',
            isGenerating: false
          }));

          // Save BPMN to local state so it shows in BPMN Canvas Panel
          if (activeProjectId) {
            await updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMA_SKILL', bpmn, false);
          }

          // Complete audit log with artifact size
          const artifactSize = new Blob([bpmn]).size;
          await completeGeneration(auditId, artifactSize);

          // Refetch audit logs to show the backend-generated entry with accurate timing
          await fetchLogs(selectedArtifactId);

          // Update process monitor with completion and actual model
          updateProcess(jobId, {
            status: 'completed',
            modelInfo: {
              provider: modelProvider as 'openai' | 'anthropic',
              selectedModel: modelName || 'unknown',
              actualModel: job.actual_model || undefined
            }
          });

          // Switch to BPMN stage
          setActiveStage('LUMA_SKILL');

          // Show success message with timing
          const durationText = attempts < 60 ? `${attempts} seconds` : `${Math.floor(attempts / 60)}m ${attempts % 60}s`;
          addToast('success', `BPMN generated successfully in ${durationText}`, 5000);

        } else if (job.status === 'failed') {
          // Update process monitor with failure
          updateProcess(jobId, {
            status: 'failed',
            error: job.error_message || 'BPMN generation failed'
          });
          throw new Error(job.error_message || 'BPMN generation failed');
        }
        // Status is still 'pending' or 'processing', continue polling
      }

      if (!jobCompleted) {
        // Timeout reached - job may still be processing in background
        console.warn(`BPMN generation timeout after ${maxAttempts} seconds. Job ID: ${jobId}`);

        // Update process monitor with timeout
        updateProcess(jobId, {
          status: 'timeout',
          error: `Timeout after ${Math.floor(maxAttempts / 60)} minutes`
        });

        throw new Error(
          `BPMN generation is taking longer than expected (${Math.floor(maxAttempts / 60)} minutes). ` +
          `The generation may still be running in the background. Please refresh the skill in a few minutes to check if it completed. ` +
          `Job ID: ${jobId}`
        );
      }

    } catch (error: any) {
      console.error('Generate BPMN error:', error);

      // Fail audit log
      await failGeneration(auditId, error.message || 'Failed to generate BPMN');

      // Update process monitor if not already updated (fallback for unexpected errors)
      // This ensures errors like network failures are also tracked
      if (jobId) {
        const isTimeout = error.message.includes('taking longer than expected');
        updateProcess(jobId, {
          status: isTimeout ? 'timeout' : 'failed',
          error: error.message || 'Failed to generate BPMN'
        });
      }

      // Show more helpful error message for timeouts
      const isTimeout = error.message.includes('taking longer than expected');
      const duration = isTimeout ? 10000 : 8000; // Longer display for timeout messages

      addToast('error', error.message || 'Failed to generate BPMN', duration);

      setGenerationState((prev) => ({
        ...prev,
        isGenerating: false,
        error: error.message || 'Failed to generate BPMN'
      }));
    }
  };

  // Approval handlers
  const approvePlan = () => {
    // After approving, reset to initial state so AI Assistant doesn't show stale content
    setGenerationState({
      currentStage: 'initial',
      planApproved: false,
      scriptApproved: false,
      bpmnApproved: false,
      isGenerating: false,
      generatedPlan: undefined,
      generatedScript: undefined
    });
  };

  const rejectPlan = () => {
    setGenerationState((prev) => ({
      ...prev,
      generatedPlan: undefined,
      currentStage: 'initial'
    }));
  };

  const approveScript = () => {
    // After approving, reset to initial state so AI Assistant doesn't show stale content
    setGenerationState({
      currentStage: 'initial',
      planApproved: false,
      scriptApproved: false,
      bpmnApproved: false,
      isGenerating: false,
      generatedPlan: undefined,
      generatedScript: undefined
    });
  };

  const rejectScript = () => {
    setGenerationState((prev) => ({
      ...prev,
      generatedScript: undefined,
      currentStage: 'plan'
    }));
  };

  const reset = () => {
    setGenerationState({
      currentStage: 'initial',
      planApproved: false,
      scriptApproved: false,
      bpmnApproved: false,
      isGenerating: false
    });
    setUserIntent('');
    setStreamedContent('');
  };

  // Render stage content
  const renderStageContent = () => {
    if (generationState.isGenerating) {
      // Get active generation for elapsed time
      const activeGen = selectedArtifactId ? getActiveGeneration(selectedArtifactId) : null;
      const elapsedMs = activeGen ? Date.now() - activeGen.startedAt : 0;
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;
      const elapsedText = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <p className="text-sm">Generating {generationState.currentStage}...</p>
          {activeGen && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <Clock className="w-3 h-3" />
              <span>{elapsedText}</span>
            </div>
          )}
          {streamedContent && (
            <div className="mt-4 w-full max-w-2xl p-4 bg-muted rounded-lg text-left">
              <pre className="text-xs whitespace-pre-wrap font-mono">{streamedContent}</pre>
            </div>
          )}
        </div>
      );
    }

    if (generationState.error) {
      const isConfigError = generationState.error.includes('Configuration Error');

      return (
        <div className="flex flex-col items-center justify-center h-full px-6">
          <XCircle className="w-8 h-8 text-destructive mb-4" />
          <div className={`text-sm ${isConfigError ? 'text-yellow-600 dark:text-yellow-500' : 'text-destructive'} text-center whitespace-pre-line max-w-md`}>
            {generationState.error}
          </div>
          <div className="flex gap-2 mt-6">
            {isConfigError && (
              <Button onClick={() => {
                const settingsButton = document.querySelector('[title="Settings"]') as HTMLElement;
                if (settingsButton) {
                  settingsButton.click();
                }
              }} variant="default" className="gap-2">
                <Settings className="w-4 h-4" />
                Open Settings
              </Button>
            )}
            <Button onClick={reset} variant="outline">
              Start Over
            </Button>
          </div>
        </div>
      );
    }

    // Stage: Plan
    if (generationState.currentStage === 'plan' && generationState.generatedPlan) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto p-4">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h4 className="text-sm font-semibold">Generated Plan</h4>
              {generationState.planApproved && (
                <span className="text-xs text-green-600 dark:text-green-500">✓ Approved</span>
              )}
            </div>
            <div className="bg-muted rounded-lg p-4">
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {generationState.generatedPlan}
              </pre>
            </div>
          </div>
          <div className="border-t border-border p-4">
            {!generationState.planApproved ? (
              <div className="flex gap-2 mb-2">
                <Button onClick={approvePlan} className="flex-1">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Plan
                </Button>
                <Button onClick={rejectPlan} variant="destructive" className="flex-1">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            ) : (
              <Button onClick={generateScript} className="w-full mb-2" variant="default">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate LumaScript
              </Button>
            )}
            <Button onClick={reset} variant="ghost" className="w-full">
              Start Over
            </Button>
          </div>
        </div>
      );
    }

    // Stage: Script
    if (generationState.currentStage === 'script' && generationState.generatedScript) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto p-4">
            <div className="mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h4 className="text-sm font-semibold">Generated LumaScript</h4>
              {generationState.scriptApproved && (
                <span className="text-xs text-green-600 dark:text-green-500">✓ Approved</span>
              )}
            </div>
            <div className="bg-muted rounded-lg p-4">
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {generationState.generatedScript}
              </pre>
            </div>
          </div>
          <div className="border-t border-border p-4">
            {!generationState.scriptApproved ? (
              <div className="flex gap-2 mb-2">
                <Button onClick={approveScript} className="flex-1">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Script
                </Button>
                <Button onClick={rejectScript} variant="destructive" className="flex-1">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            ) : (
              <Button onClick={generateBPMN} className="w-full mb-2" variant="default">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Preview
              </Button>
            )}
            <Button onClick={reset} variant="ghost" className="w-full">
              Start Over
            </Button>
          </div>
        </div>
      );
    }

    // Stage: Complete
    if (generationState.currentStage === 'complete') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground px-6">
          <CheckCircle className="w-16 h-16 text-green-500 mb-6" />
          <p className="text-base font-semibold mb-3 text-foreground">Skill Generation Complete!</p>
          <p className="text-sm mb-8 text-center max-w-xs leading-relaxed">
            Preview has been generated and saved to your skill.
          </p>
          <Button onClick={reset} variant="outline" className="min-w-[200px]">
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Another Skill
          </Button>
        </div>
      );
    }

    // Stage: Initial
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center max-w-md px-4">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-primary" />
          <p className="text-sm font-semibold mb-2">AI-Assisted Skill Generation</p>
          <p className="text-xs mb-4">
            Describe what you want your skill to do, and the AI will guide you through creating
            a Plan, LumaScript, and Preview workflow.
          </p>

          {/* Proactive API Key Warning */}
          {hasApiKey === false && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
                <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-400">
                  No API Key Configured
                </p>
              </div>
              <p className="text-xs text-yellow-700 dark:text-yellow-500 mb-3">
                AI generation requires an OpenAI or Anthropic API key. Please configure your API keys in Settings.
              </p>
              <Button
                onClick={() => {
                  const settingsButton = document.querySelector('[title="Settings"]') as HTMLElement;
                  if (settingsButton) {
                    settingsButton.click();
                  }
                }}
                size="sm"
                variant="default"
                className="w-full gap-2"
              >
                <Settings className="w-3 h-3" />
                Open Settings
              </Button>
            </div>
          )}

          {!activeProjectId && (
            <p className="text-xs text-yellow-600 dark:text-yellow-500">
              ⚠️ Please select or create a project first
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="panel-container">
      <div className="panel-header">
        <div>
          <h3 className="text-sm font-semibold">AI Assistant</h3>
          <p className="text-xs text-muted-foreground">
            {generationState.currentStage === 'initial'
              ? 'Ready to generate'
              : `Stage: ${generationState.currentStage}`}
          </p>
        </div>
      </div>

      <div className="panel-content overflow-hidden">
        <div className="flex flex-col h-full relative">
          {/* Stage Content - different behavior for initial vs other stages */}
          {generationState.currentStage === 'initial' ? (
            <div ref={contentRef} className="overflow-auto flex-1 pb-28">
              {renderStageContent()}
            </div>
          ) : (
            <div ref={contentRef} className="flex-1 overflow-auto min-h-0">
              {renderStageContent()}
            </div>
          )}

          {/* Input (only show in initial stage) - ANCHORED AT BOTTOM */}
          {generationState.currentStage === 'initial' && !generationState.isGenerating && (
            <div className="absolute bottom-0 left-0 right-0 border-t border-border p-3 bg-background">
              <div className="flex flex-col gap-2">
                <textarea
                  value={userIntent}
                  onChange={(e) => setUserIntent(e.target.value)}
                  placeholder={isProjectContext ? "Describe your automation project..." : "Describe your skill..."}
                  disabled={generationState.isGenerating || !activeProjectId}
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <Button
                  onClick={generatePlan}
                  disabled={generationState.isGenerating || !userIntent.trim() || !activeProjectId}
                  className="w-full"
                  size="sm"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {isProjectContext ? 'Generate Master Plan' : 'Generate Artifact Plan'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
